import { useState, useEffect, useRef } from 'react';

const steps = [
  {
    number: '01',
    title: 'We Deliver',
    description:
      "Schedule online and we'll drop off a stack of clean, sturdy reusable totes right at your door — before moving day.",
    icon: 'ri-truck-line',
    color: 'bg-yellow-400',
    detail: 'Free delivery within our West Michigan service area',
  },
  {
    number: '02',
    title: 'You Pack',
    description:
      'Fill the stackable totes instead of wrestling with cardboard boxes and tape. They\'re strong, lidded, and easy to carry.',
    icon: 'ri-inbox-archive-line',
    color: 'bg-yellow-400',
    detail: 'Each tote holds up to 65 lbs — no tape needed',
  },
  {
    number: '03',
    title: 'You Move',
    description:
      'Totes stack perfectly in moving trucks, saving space and keeping your belongings safe and organized during the move.',
    icon: 'ri-truck-fill',
    color: 'bg-yellow-400',
    detail: 'Stackable design saves up to 30% more truck space',
  },
  {
    number: '04',
    title: 'We Pick Up',
    description:
      "Once you're settled in, we come back to collect the empty totes. No recycling runs, no cardboard piles — just done.",
    icon: 'ri-checkbox-circle-line',
    color: 'bg-yellow-400',
    detail: 'Flexible pickup scheduling at your convenience',
  },
];

export default function HowItWorks() {
  const [visibleSteps, setVisibleSteps] = useState<boolean[]>(
    new Array(steps.length).fill(false)
  );
  const sectionRef = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          steps.forEach((_, i) => {
            setTimeout(() => {
              setVisibleSteps((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, i * 200);
          });
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="py-28 bg-white overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div className="text-center mb-20">
          <span className="inline-block bg-yellow-100 text-yellow-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 tracking-wide uppercase">
            Simple Process
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
            Moving Made Easy in<br />
            <span className="text-yellow-400">4 Simple Steps</span>
          </h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            Heavy duty totes delivered to you and picked back up so you can focus on moving.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">

          {/* Connector line (desktop) */}
          <div className="hidden lg:block absolute top-16 left-0 right-0 h-0.5 bg-gray-100 z-0" style={{ top: '64px' }}>
            <div className="h-full bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-300 opacity-60" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center"
                style={{
                  opacity: visibleSteps[index] ? 1 : 0,
                  transform: visibleSteps[index] ? 'translateY(0)' : 'translateY(32px)',
                  transition: 'opacity 0.5s ease, transform 0.5s ease',
                }}
              >
                {/* Icon circle */}
                <div className="relative mb-6">
                  <div className="w-32 h-32 rounded-full bg-yellow-50 border-4 border-yellow-200 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                    <div className="w-20 h-20 rounded-full bg-yellow-400 flex items-center justify-center shadow-inner">
                      <i className={`${step.icon} text-4xl text-gray-900`} />
                    </div>
                  </div>
                  {/* Step number badge */}
                  <div className="absolute -top-2 -right-2 w-9 h-9 rounded-full bg-gray-900 text-white text-xs font-bold flex items-center justify-center shadow-lg">
                    {step.number}
                  </div>
                  {/* Arrow connector (desktop, between steps) */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:flex absolute top-1/2 -right-12 -translate-y-1/2 w-8 h-8 items-center justify-center text-yellow-400 z-20">
                      <i className="ri-arrow-right-s-line text-3xl" />
                    </div>
                  )}
                </div>

                {/* Card */}
                <div className="bg-gray-50 rounded-2xl p-6 w-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{step.description}</p>
                  <div className="flex items-start gap-2 bg-yellow-50 rounded-lg px-3 py-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-check-line text-yellow-600 text-sm" />
                    </div>
                    <p className="text-xs text-yellow-700 font-medium text-left">{step.detail}</p>
                  </div>
                </div>

                {/* Mobile arrow */}
                {index < steps.length - 1 && (
                  <div className="flex lg:hidden w-8 h-8 items-center justify-center text-yellow-400 my-2">
                    <i className="ri-arrow-down-s-line text-3xl" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom strip */}
        <div className="mt-16 bg-gray-900 rounded-2xl px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-leaf-line text-yellow-400 text-2xl" />
            </div>
            <p className="text-white font-semibold text-lg">
              No cardboard waste. No recycling piles. Just a cleaner move.
            </p>
          </div>
          <a
            href="#booking"
            className="whitespace-nowrap bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-bold px-6 py-3 rounded-xl transition-colors cursor-pointer text-sm"
          >
            Book Your Delivery
          </a>
        </div>

      </div>
    </section>
  );
}
